import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, QrCodeIcon, SendIcon } from 'lucide-react';
import { CardLogos } from '../components/payment/CardLogos';
import { PaymentForm } from '../components/payment/PaymentForm';
import { OTPForm } from '../components/payment/OTPForm';
import { ProcessingAnimation } from '../components/payment/ProcessingAnimation';
import { RaastQRPayment } from '../components/payment/RaastQRPayment';
import { RaastRTPPayment } from '../components/payment/RaastRTPPayment';
import { Modal } from '../components/ui/Modal';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../utils/formatters';
type PaymentStep = 'details' | 'otp' | 'processing';
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    subtotal,
    shippingCost,
    total,
    itemCount,
    clearCart
  } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [raastSubMethod, setRaastSubMethod] = useState<'qr' | 'rtp'>('qr');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Payment Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('details');
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentData, setPaymentData] = useState<any>(null);
  const [savedTotal, setSavedTotal] = useState(0);
  // Redirect to cart if empty and not currently processing an order
  useEffect(() => {
    if (itemCount === 0 && !isPaymentModalOpen && !isSubmitting) {
      navigate('/cart');
    }
  }, [itemCount, navigate, isPaymentModalOpen, isSubmitting]);
  // Save total when modal opens so it persists through cart clear
  useEffect(() => {
    if (isPaymentModalOpen && total > 0) {
      setSavedTotal(total);
    }
  }, [isPaymentModalOpen, total]);
  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentMethod === 'card' || paymentMethod === 'raast') {
      // Open payment modal for card or RAAST payments
      setPaymentStep('details');
      setIsPaymentModalOpen(true);
    } else {
      // Handle COD directly
      setIsSubmitting(true);
      setTimeout(() => {
        const orderId = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
        clearCart();
        navigate('/order-confirmation', {
          state: {
            orderId,
            amount: total,
            paymentMethod: 'Cash on Delivery',
            paymentStatus: 'Pending'
          }
        });
      }, 1000);
    }
  };
  // --- Payment Modal Handlers ---
  const handlePaymentSubmit = (data: any) => {
    setPaymentData(data);
    setPaymentStep('otp');
  };
  const handleOTPSubmit = (otp: string) => {
    setPaymentStep('processing');
    setPaymentStatus('processing');
    // Simulate API call
    setTimeout(() => {
      setPaymentStatus('success');
    }, 4000);
  };
  const handlePaymentCancel = () => {
    setIsPaymentModalOpen(false);
    setPaymentStep('details');
    setIsSubmitting(false);
  };
  const handlePaymentComplete = () => {
    setIsSubmitting(true);
    setIsPaymentModalOpen(false);
    const orderId = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
    const finalAmount = savedTotal || total;
    const finalCardLast4 = paymentMethod === 'card' ? paymentData?.cardNumber?.slice(-4) || '****' : undefined;
    const finalPaymentMethod = paymentMethod === 'raast' ? raastSubMethod === 'qr' ? 'RAAST QR' : 'RAAST Request to Pay' : 'Credit/Debit Card';
    clearCart();
    navigate('/order-confirmation', {
      state: {
        orderId,
        amount: finalAmount,
        paymentMethod: finalPaymentMethod,
        paymentStatus: 'Paid',
        receiptNo: `REC-${Math.floor(10000000 + Math.random() * 90000000)}`,
        date: new Date().toISOString(),
        cardLast4: finalCardLast4
      }
    });
  };
  return <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8" data-id="element-1104">
      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1105">Checkout</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8" data-id="element-1106">
        <div className="md:col-span-8" data-id="element-1107">
          <form onSubmit={handlePlaceOrder} className="space-y-8" data-id="element-1108">
            {/* Shipping Info */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-1109">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1110">
                Shipping Information
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-1111">
                <div data-id="element-1112">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1113">
                    First Name
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="John" required data-id="element-1114" />
                </div>
                <div data-id="element-1115">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1116">
                    Last Name
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="Doe" required data-id="element-1117" />
                </div>
                <div className="col-span-2" data-id="element-1118">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1119">
                    Address
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="123 Main St" required data-id="element-1120" />
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-1121">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1122">Payment Method</h2>

              <div className="space-y-4" data-id="element-1123">
                {/* Credit/Debit Card Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1124">
                  <div className="flex items-start" data-id="element-1125">
                    <div className="flex items-center h-5" data-id="element-1126">
                      <input type="radio" name="paymentMethod" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1127" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1128">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1129">
                        Credit/Debit Card
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1130">
                        Pay securely with your card via Paysys Labs gateway.
                      </span>
                      <CardLogos data-id="element-1131" />
                    </div>
                  </div>
                </label>

                {/* RAAST Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'raast' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1132">
                  <div className="flex items-start" data-id="element-1133">
                    <div className="flex items-center h-5" data-id="element-1134">
                      <input type="radio" name="paymentMethod" value="raast" checked={paymentMethod === 'raast'} onChange={() => setPaymentMethod('raast')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1135" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1136">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1137">
                        RAAST
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1138">
                        Pay instantly via RAAST QR or Request to Pay.
                      </span>
                      <div className="mt-3" data-id="element-1139">
                        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-6 object-contain" data-id="element-1140" />
                      </div>

                      {/* RAAST Sub-options */}
                      {paymentMethod === 'raast' && <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-3" data-id="element-1141">
                          <button type="button" onClick={e => {
                        e.preventDefault();
                        setRaastSubMethod('qr');
                      }} className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${raastSubMethod === 'qr' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1142">
                            {raastSubMethod === 'qr' && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-1143" />}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${raastSubMethod === 'qr' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1144">
                              <QrCodeIcon className="w-5 h-5" data-id="element-1145" />
                            </div>
                            <div className="text-center" data-id="element-1146">
                              <p className={`text-xs font-semibold ${raastSubMethod === 'qr' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1147">
                                RAAST QR
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-1148">
                                Scan & Pay
                              </p>
                            </div>
                          </button>
                          <button type="button" onClick={e => {
                        e.preventDefault();
                        setRaastSubMethod('rtp');
                      }} className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${raastSubMethod === 'rtp' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1149">
                            {raastSubMethod === 'rtp' && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-1150" />}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${raastSubMethod === 'rtp' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1151">
                              <SendIcon className="w-5 h-5" data-id="element-1152" />
                            </div>
                            <div className="text-center" data-id="element-1153">
                              <p className={`text-xs font-semibold ${raastSubMethod === 'rtp' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1154">
                                Request to Pay
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-1155">
                                IBAN / Mobile
                              </p>
                            </div>
                          </button>
                        </div>}
                    </div>
                  </div>
                </label>

                {/* Cash on Delivery Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1156">
                  <div className="flex items-center" data-id="element-1157">
                    <div className="flex items-center h-5" data-id="element-1158">
                      <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1159" />
                    </div>
                    <div className="ml-3" data-id="element-1160">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1161">
                        Cash on Delivery
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1162">
                        Pay when you receive your order.
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end" data-id="element-1163">
              <button type="submit" disabled={isSubmitting || itemCount === 0} className="bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-1164">
                {isSubmitting ? 'Processing...' : paymentMethod === 'card' || paymentMethod === 'raast' ? 'Proceed to Payment' : 'Place Order'}
              </button>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="md:col-span-4" data-id="element-1165">
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 sticky top-6" data-id="element-1166">
            <h2 className="text-lg font-semibold mb-4" data-id="element-1167">Order Summary</h2>
            <div className="space-y-3 text-sm" data-id="element-1168">
              <div className="flex justify-between" data-id="element-1169">
                <span className="text-gray-600" data-id="element-1170">
                  Subtotal ({itemCount} items)
                </span>
                <span className="font-medium" data-id="element-1171">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between" data-id="element-1172">
                <span className="text-gray-600" data-id="element-1173">Shipping</span>
                <span className="font-medium" data-id="element-1174">
                  {shippingCost === 0 ? <span className="text-green-600" data-id="element-1175">Free</span> : formatPrice(shippingCost)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg" data-id="element-1176">
                <span data-id="element-1177">Total</span>
                <span data-id="element-1178">{formatPrice(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal Overlay */}
      <Modal isOpen={isPaymentModalOpen} onClose={handlePaymentCancel} size="lg" data-id="element-1179">
        <div className="-m-4 flex flex-col font-sans overflow-hidden rounded-2xl" data-id="element-1180">
          {/* Paysys Labs Header */}
          <header className="bg-white border-b border-gray-200 py-4 px-6 shadow-sm" data-id="element-1181">
            <div className="flex justify-between items-center" data-id="element-1182">
              <div className="flex items-center space-x-2" data-id="element-1183">
                <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-7 object-contain" data-id="element-1184" />
              </div>
              <div className="flex items-center text-sm text-gray-500" data-id="element-1185">
                <Shield className="w-4 h-4 mr-1 text-green-500" data-id="element-1186" />
                <span className="hidden sm:inline" data-id="element-1187">Secure Checkout</span>
              </div>
            </div>
          </header>

          {/* Progress Bar */}
          <div className="h-1 w-full bg-gray-100" data-id="element-1188">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-in-out" style={{
            width: paymentMethod === 'raast' ? '100%' : paymentStep === 'details' ? '33%' : paymentStep === 'otp' ? '66%' : '100%'
          }} data-id="element-1189" />
          </div>

          {/* Main Content Area */}
          <div className="p-6 sm:p-8 bg-white min-h-[400px] flex flex-col justify-center" data-id="element-1190">
            {paymentMethod === 'raast' ? raastSubMethod === 'qr' ? <RaastQRPayment totalAmount={total} onComplete={handlePaymentComplete} onCancel={handlePaymentCancel} data-id="element-1191" /> : <RaastRTPPayment totalAmount={total} onComplete={handlePaymentComplete} onCancel={handlePaymentCancel} data-id="element-1192" /> : <>
                {paymentStep === 'details' && <>
                    <div className="mb-6" data-id="element-1193">
                      <h2 className="text-2xl font-bold text-gray-900" data-id="element-1194">
                        Payment Details
                      </h2>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1195">
                        Enter your card information to complete the purchase.
                      </p>
                    </div>
                    <PaymentForm onSubmit={handlePaymentSubmit} onCancel={handlePaymentCancel} totalAmount={total} data-id="element-1196" />
                  </>}

                {paymentStep === 'otp' && <OTPForm onSubmit={handleOTPSubmit} onCancel={handlePaymentCancel} amount={savedTotal || total} cardLast4={paymentData?.cardNumber?.replace(/\s/g, '').slice(-4) || '****'} cardScheme={(() => {
              const num = paymentData?.cardNumber?.replace(/\D/g, '') || '';
              if (num.startsWith('2')) return 'PayPak';
              if (num.startsWith('4')) return 'Visa';
              if (num.startsWith('5')) return 'Mastercard';
              if (num.startsWith('6')) return 'UnionPay';
              return 'Visa';
            })()} data-id="element-1197" />}

                {paymentStep === 'processing' && <ProcessingAnimation status={paymentStatus} onComplete={handlePaymentComplete} data-id="element-1198" />}
              </>}
          </div>

          {/* Footer inside modal */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-1199">
            <div className="flex items-center text-xs text-gray-500" data-id="element-1200">
              <Lock className="w-3 h-3 mr-1" data-id="element-1201" />
              256-bit SSL Encryption
            </div>
            <div className="flex items-center space-x-2" data-id="element-1202">
              <img src="https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png" alt="Visa" className="h-4 opacity-60" data-id="element-1203" />
              <img src="https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png" alt="Mastercard" className="h-4 opacity-60" data-id="element-1204" />
              <img src="https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png" alt="UnionPay" className="h-4 opacity-60" data-id="element-1205" />
              <img src="https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png" alt="PayPak" className="h-4 opacity-60" data-id="element-1206" />
              <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-4 opacity-60" data-id="element-1207" />
            </div>
          </div>
        </div>
      </Modal>
    </div>;
}